import { useRef } from 'react'
import type { WireArticleBlock } from '../../../api/types'
import { useArticleArtifacts } from '../common/ArticleArtifactsContext'
import { useVisualEngagementContext } from '../common/VisualEngagementContext'
import { emitVisualInteraction, useVisualInViewTracking } from '../common/visualCollectionTracking'
import { PortionMark } from '../common/PortionMark'

type Block = Extract<WireArticleBlock, { type: 'video' }>

const WIDTH_CLASS: Record<NonNullable<Block['width']>, string> = {
  'column': 'mx-auto max-w-3xl',
  'wide':   'mx-auto max-w-4xl',
  'full':   'w-full',
}

export function VideoBlock({ block }: { block: Block }) {
  const artifacts = useArticleArtifacts()
  const engagement = useVisualEngagementContext()
  const figureRef = useRef<HTMLElement>(null)

  const { width = 'column', autoplay = false, loop = false, muted = false } = block
  const visualId = block.artifactId || block.id

  useVisualInViewTracking(engagement, figureRef, visualId, 'VIDEO')

  const videoSrc    = artifacts.find(a => a.id === block.artifactId)?.presignedUrl ?? ''
  const posterSrc   = block.posterArtifactId
    ? (artifacts.find(a => a.id === block.posterArtifactId)?.presignedUrl ?? undefined)
    : undefined
  const captionsSrc = block.captionsArtifactId
    ? (artifacts.find(a => a.id === block.captionsArtifactId)?.presignedUrl ?? undefined)
    : undefined

  return (
    <figure ref={figureRef} className={`my-0 ${WIDTH_CLASS[width]}`}>
      <video
        src={videoSrc}
        poster={posterSrc}
        controls
        autoPlay={autoplay}
        loop={loop}
        muted={muted || autoplay}
        playsInline
        aria-label={block.altText}
        className="w-full rounded bg-black"
        onPointerEnter={() => emitVisualInteraction(engagement, visualId, 'VIDEO', 'HOVER_OVER')}
        onClick={() => emitVisualInteraction(engagement, visualId, 'VIDEO', 'CLICK')}
        onPlay={() => emitVisualInteraction(engagement, visualId, 'VIDEO', 'PLAY')}
        onPause={() => emitVisualInteraction(engagement, visualId, 'VIDEO', 'PAUSE')}
        onEnded={() => emitVisualInteraction(engagement, visualId, 'VIDEO', 'COMPLETE')}
        onSeeked={() => emitVisualInteraction(engagement, visualId, 'VIDEO', 'SEEK')}
      >
        {captionsSrc && (
          <track kind="subtitles" src={captionsSrc} srcLang="en" default />
        )}
      </video>
      {(block.caption || block.credit) && (
        <figcaption className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-fg-secondary">
          {block.credit && (
            <>
              <span className="italic opacity-70">{block.credit}</span>
              {block.caption && <span aria-hidden="true">·</span>}
            </>
          )}
          {block.caption && (
            <>
              <PortionMark
                classification={block.captionClassification}
                disseminationControls={block.captionDisseminationControls}
              />
              {block.caption}
            </>
          )}
        </figcaption>
      )}
    </figure>
  )
}
